// src/app/hooks/fan/queries.ts
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useGetFanIsolatorsSpringParamsQuery,
  useGetFanMotorBaseParamsQuery,
  useGetFanMountingsQuery,
  useGetFanParamsQuery,
  useGetFanSpacingSidesQuery,
  useGetFanSpacingTopBtmQuery,
  useGetSystemEffectQuery,
  useLazyGetFanModelQuery,
  useLazyGetFanMotorsQuery,
  useLazyGetFanQuery,
  useLazyGetMotorsCountQuery,
} from '../../services';
import {
  setFan,
  setFanIsolatorsSpringParams,
  setFanMotorBaseParams,
  setFanParams,
  setFanSpacings,
} from '../../slices';
import { useContextValue } from '../../components/molecule/WithContext';
import { getQueryString } from '../../helpers/index';
import { IFan, IRatedFan } from '../../models';
import type { IState } from '../../../../models';

export const useGetFanMainData = (id: number) => {
  const { isLoading: fanLoading } = useGetFan(id);
  const { isLoading } = useGetFanParams();
  const { isLoading: baseLoading } = useGetFanMotorBaseParams();
  const { isLoading: isolatorsLoading } = useGetFanIsolatorsSpringParams();

  return {
    isLoading: isLoading || fanLoading || baseLoading || isolatorsLoading,
  };
};

export const useGetFan = (id: number, fetch = true) => {
  const dispatch = useDispatch();
  const [getFan, { data: fan, isLoading }] = useLazyGetFanQuery();

  useEffect(() => {
    fetch && id && getFan(id);
  }, [id]);

  useEffect(() => {
    if (fan) dispatch(setFan(fan));
  }, [fan]);

  return { fan, isLoading, getFan };
};

export const useGetFanParams = (fetch_all = true) => {
  const dispatch = useDispatch();

  const { data: params, isLoading, refetch } = useGetFanParamsQuery();

  // Conditionally fetch additional data
  const { data: system_effect } = useGetSystemEffectQuery(undefined, {
    skip: !fetch_all,
  });
  const { data: mountings } = useGetFanMountingsQuery(undefined, {
    skip: !fetch_all,
  });

  useEffect(() => {
    if (!params) return;

    if (fetch_all && system_effect && mountings) {
      dispatch(setFanParams({ ...params.fan, system_effect, mountings }));
    } else {
      dispatch(setFanParams(params.fan));
    }
  }, [fetch_all, params, system_effect, mountings, dispatch]);

  return { isLoading, params, get: refetch };
};


export const useGetFaSpacings = () => {
  const dispatch = useDispatch();

  const { data: sides } = useGetFanSpacingSidesQuery();
  // Was a second call to the SIDES query — invisible while both tables hold the
  // same eight rows, wrong the moment they diverge.
  const { data: top_btm } = useGetFanSpacingTopBtmQuery();

  useEffect(() => {
    if (top_btm && sides) {
      dispatch(setFanSpacings({ top_btm, sides }));
    }
  }, [sides, top_btm]);
};

export const useGetFanMotorBaseParams = () => {
  const dispatch = useDispatch();

  const { data: params, isLoading, refetch } = useGetFanMotorBaseParamsQuery();

  useEffect(() => {
    if (params) {
      dispatch(setFanMotorBaseParams(params));
    }
  }, [params]);

  return { isLoading, params, get: refetch };
};

export const useGetFanIsolatorsSpringParams = () => {
  const dispatch = useDispatch();

  const {
    data: params,
    isLoading,
    refetch,
  } = useGetFanIsolatorsSpringParamsQuery();

  useEffect(() => {
    if (params) {
      dispatch(setFanIsolatorsSpringParams(params));
    }
  }, [params]);

  return { isLoading, params, get: refetch };
};

export const getMotorCount = () => {
  const { setExtraOptions } = useContextValue();
  const [getCount] = useLazyGetMotorsCountQuery();
  const getData = async (fan: IFan, ratedFan?: IRatedFan) => {
    if (!fan.params?.arr_id) return;
    const start_with = fan.params?.mfg_id == 67 ? 'motor_' : '';
    const filteringFields = {
      [`${start_with}mfg_id`]: fan.motorParams?.mfg_id,
      [`${start_with}series_id`]: fan.motorParams?.series_id,
      efficiency_id: fan.motorParams?.efficiency_id,
      enclosure_id: fan.motorParams?.enclosure_id,
      housing_id: fan.motorParams?.housing_id,
      motor_power: fan.motor_power,
      speed_nominal_id: fan.motorParams?.speed_nominal_id,
      type_id: fan.motorParams?.type_id,
      shaft_grounding_id: fan.motorParams?.shaft_grounding_id,
      fan_type_id: fan.params?.type_id,
      fan_arr_id: fan.params?.arr_id,
      fan_mfg_id: fan.params?.mfg_id,
      fan_series_id: fan.params?.series_id,
      fan_amca_class_id: fan.params?.amca_class_id,
      voltage_id: fan.motor_voltage_id,
      speed: fan.speed || ratedFan?.speed,
    };
    const queryString = getQueryString(filteringFields, true);
    const { data } = await getCount(queryString);
    setExtraOptions((draft: any) => {
      draft.avaible_motors_count = data?.count || 0;
    });
  };

  return getData;
};

export const getFanMotorsData = () => {
  const [getFanMotors] = useLazyGetFanMotorsQuery();
  const getData = async (fan: IFan, ratedFan?: IRatedFan) => {
    const start_with = fan.params?.mfg_id == 67 ? 'motor_' : '';
    const filteringFields = {
      [`${start_with}mfg_id`]: fan.motorParams?.mfg_id,
      [`${start_with}series_id`]: fan.motorParams?.series_id,
      efficiency_id: fan.motorParams?.efficiency_id,
      enclosure_id: fan.motorParams?.enclosure_id,
      housing_id: fan.motorParams?.housing_id,
      motor_power: fan.motor_power,
      speed_nominal_id: fan.motorParams?.speed_nominal_id,
      type_id: fan.motorParams?.type_id,
      shaft_grounding_id: fan.motorParams?.shaft_grounding_id,
      fan_type_id: fan.params?.type_id,
      fan_arr_id: fan.params?.arr_id,
      fan_mfg_id: fan.params?.mfg_id,
      fan_series_id: fan.params?.series_id,
      fan_amca_class_id: fan.params?.amca_class_id,
      voltage_id: fan.motor_voltage_id,
      speed: fan.speed || ratedFan?.speed,
      rating: true,
      fan_id: ratedFan?.fan_id,
      fan_wheel_dia_nominal_id: fan?.params.wheel_dia_nominal_id,
      fan_bhp: ratedFan?.bhp,
    };
    const queryString = getQueryString(filteringFields, true);
    const { data } = await getFanMotors(queryString);
    return data;
  };

  return getData;
};

export const getSelectedFanModel = () => {
  const [getFan] = useLazyGetFanModelQuery();
  const params = useSelector((state: IState) => state.fan.fan_params);

  const getData = async (fan: IFan, ratedFan?: IRatedFan) => {
    if (
      fan.params?.mfg_id == 164 ||
      fan.params?.mfg_id == 37 ||
      fan.params?.mfg_id == 67 ||
      fan.params?.mfg_id == 23
    )
      return null;
    const filteringFields = {
      page: 1,
      spn: ratedFan?.model || ratedFan?.name,
      mfg_id: fan.params?.mfg_id,
      series_id: fan.params?.series_id,
      type_id: fan.params?.type_id,
      arr_id: fan.params?.arr_id,
      amca_class_id:
        fan.params?.amca_class_id ||
        params?.amca_classes.find((e) => e.name == ratedFan?.amca_class)?.id,
      wheel_size_id: params?.wheel.size.find(
        (e) => e.value == ratedFan?.wheel_size
      )?.id,
      rotation_id: fan.params.rotation_id,
      wheel_dia_percent_id:
        fan.params.wheel_dia_percent_id ||
        params?.wheel.dia_percent.find(
          (e) => e.value == ratedFan?.wheel_dia_percent
        )?.id,
      wheel_dia_nominal_id:
        fan.params.wheel_dia_nominal_id ||
        params?.wheel.nominal_diameter.find(
          (e) => e.value == ratedFan?.wheel_dia_nominal
        )?.id,
      wheel_width_percent_id:
        fan.params.wheel_width_percent_id ||
        params?.wheel.width_percent.find(
          (e) => e.value == ratedFan?.wheel_width_percent
        )?.id,
      redundancy_id: fan.params.redundancy_id,
    };
    const queryString = getQueryString(filteringFields, true);
    const { data } = await getFan(queryString);
    return data?.[0];
  };

  return getData;
};

export const useGetFanMotor = () => {
  const [getFanMotors] = useLazyGetFanMotorsQuery();

  const getFanMotor = async (
    motorParams: any
  ): Promise<any> => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', '1');
    Object.keys(motorParams).forEach((key) => {
      const value = motorParams[key];
      if (value !== undefined && value !== null) {
        queryParams.append(key, value.toString());
      }
    });
    const queryString = queryParams.toString();
    try {
      const { data } = await getFanMotors(queryString);
      if (Array.isArray(data) && data.length > 0) {
        return data[0];
      }
      return null;
    } catch (e) {
      return null;
    }
  };

  return { getFanMotor };
};

export const useGetFanData = () => {
  const [getFanModel] = useLazyGetFanModelQuery();

  const getFanData = async (
    spn: string
  ): Promise<any> => {
    if (!spn) return {};
    try {
      const { data } = await getFanModel(`?&spn=${spn}`);
      if (Array.isArray(data) && data.length > 0) {
        return data[0];
      }
      return {};
    } catch (e) {
      return {};
    }
  };

  return { getFanData };
};
